import { format, parse } from 'date-fns'
import { cloneDeep, isEqual } from 'lodash'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

import ViewsLoader from '../../../../components/loader/ViewsLoader'
import { JudgingTable, OperationsHeader, PrizeMoneyTable, ScoringTableByClass } from './components'
import { OrderOfGoTableByClass } from './components/OrderOfGoTable/OrderOfGoTableByClass'
import { OrderOfGoTableByRider } from './components/OrderOfGoTable/OrderOfGoTableByRider'
import { ScoringTableByRider } from './components/ScoringTable/ScoringTableByRider'

import FirestoreService from '../../../../services/firestoreService'
import UserService from '../../../../services/firebase-service/users/userService'
import RegistrationDyDayService from '../../../../services/firebase-service/registration-by-day/RegistrationDyDayService'
import EventReviewPublishService from '../../../../services/firebase-service/event-review-publish/EventReviewPublishService'
import FirebaseStorageService from '../../../../services/storageService'

import {
  FeesTypeItem,
  IEventFees,
  IJudgeOption,
  IScoreCardDiscipline,
  RegistrationFeesType,
} from '../../../../models/event-fees/event-fees.interface'
import { EventFeesModel } from '../../../../models/event-fees/event-fees.model'
import {
  IJudge,
  IRegistrationByDayInterface,
} from '../../../../models/registrations-by-day/registrationByDay.interface'
import { IUserInterface } from '../../../../models/users/user.interface'
import { UserModel } from '../../../../models/users/user.model'

import { IBreak, IBreakRegistrationByDay } from '../../../../models/events/event.interface'
import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'
import { IManageOption } from './types/IManageOption'

import { getUserFullName } from '../../../../helpers/helpers'
import { getConvertedData } from '../../../../models/interface.helper'
import {
  getAllRegistrationFees,
  getGroupFeesByDayAndFacility,
} from '../../../../helpers/registrationFees'
import { getCurrency } from '../../../../helpers/payment'

import { selectedEvent } from '../../../../store/events/eventsSlice'
import { useAppSelector } from '../../../../store/hooks'
import { selectUserId } from '../../../../store/user/userSlice'

import {
  getJudgesAndSheet,
  sortResultsByTime,
  sortByOrder,
  sortByTotalPoints,
} from './data/operations-tab-utils'
import { httpService } from '../../../../services/httpService'
import useToasterHelper from '../../../../helpers/ToasterHelper'
import { HandleModalContext } from '../../../../layout/mainlayout/MainLayout'

import { CONST } from '../../../../const/const'
import { MANAGE_OPERATIONS_CONSTS } from '../data/operations.data.const'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../../types/payment'
import { MESSAGES_CONST } from '../../../../const/messages-const'
import { MODAL_CONSTS } from '../../../../const/modal-const'

const PATH = CONST.DATA.STORAGE.EVENTS.EVENT_SCORE.PREFIX
const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export type IOperationsTabs = 'judging' | 'order-of-go' | 'scoring' | 'prize-money'

export interface IRegistrationByDayEx extends IRegistrationByDayInterface {
  loading?: boolean
  fee?: FeesTypeItem
}

export interface RegistrationFeesTypeEx extends RegistrationFeesType {
  loading?: boolean
  parentId?: string
}

export interface IUpdateFeesKeys {
  (keys: object, selectedUuIds: { uuid: string; parentId?: string }[]): Promise<void>
}

export interface IUpdateRegistrationByDayName {
  feeUuid: string[]
  sheetId: string
  discipline: IScoreCardDiscipline
  judgesValues?: string[]
}

const ManageClinicNOtherOperationsTab = () => {
  const toastFunctions = useToasterHelper()
  const handleModalContext = useContext(HandleModalContext)

  const { eventId } = useParams<{ eventId: string }>()
  const currentEvent = useAppSelector(selectedEvent)
  const userId = useAppSelector(selectUserId)

  const [activeTab, setActiveTab] = useState<IOperationsTabs>('judging')
  const [registrationsByDayByClass, setRegistrationsByDayByClass] = useState<
    IRegistrationByDayInterface[]
  >([])
  const [breaks, setBreaks] = useState<IBreak[]>([])

  const [classOptions, setClassOptions] = useState<IManageOption[]>([])
  const [selectedClass, setSelectedClass] = useState<IManageOption | null>(null)

  const [increment, setIncrement] = useState<string>('')
  const [gapTime, setGapTime] = useState<string>('00:00:00')
  const [clashCount, setClashCount] = useState(0)
  const [searchValue, setSearchValue] = useState('')

  const [dayOptions, setDayOptions] = useState<IManageOption[]>([])
  const [selectedDay, setSelectedDay] = useState<IManageOption | null>(null)

  const [registrationFees, setRegistrationFees] = useState<IEventFees | null>(null)
  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)
  const [fee, setFee] = useState<RegistrationFeesType | null>(null)

  const [registrationsByDay, setRegistrationsByDay] = useState<IRegistrationByDayInterface[]>([])

  const [judgesOptions, setJudgesOptions] = useState<IJudgeOption[] | null>(null)
  const [scheduleBy, setScheduleBy] = useState<'class' | 'rider' | null>(null)
  const [registrationsGroupedByDay, setRegistrationsGroupedByDay] = useState<{
    [key: string]: IBreakRegistrationByDay[]
  }>({})
  const [isEditMode, setIsEditMode] = useState(false)

  const currency = getCurrency(currentEvent?.EventPaymentSettings?.currency)

  const handleSetSelectedDay = useCallback((data: IManageOption) => {
    setSelectedDay(data)
  }, [])

  const handleSetSelectedClass = useCallback((data: IManageOption) => {
    setSelectedClass(data)
  }, [])

  const handleSetRegistrationsByDay = useCallback((value: IRegistrationByDayInterface[]) => {
    setRegistrationsByDay(value)
  }, [])

  const handleSetGapTime = useCallback((value: string) => {
    setGapTime(value)
  }, [])

  const handleTabChange = useCallback(
    (newTab: IOperationsTabs) => {
      // check if trying to navigate away from order-of-go tab while in edit mode
      if (activeTab === 'order-of-go' && isEditMode && newTab !== 'order-of-go') {
        const confirmNavigation = window.confirm(
          'You have unsaved changes in edit mode. Are you sure you want to leave? Your changes will be lost.'
        )

        if (confirmNavigation) {
          // user confirmed - exit edit mode allow tab change
          setIsEditMode(false)
          setActiveTab(newTab)
        }
        // if user cancel, stay on curr tab
      } else {
        setActiveTab(newTab)
      }
    },
    [activeTab, isEditMode]
  )

  const getRegistrationsByDay = async () => {
    const registrationsByDay_ = await RegistrationDyDayService.getByEventIds([eventId])

    const uniqueRegistrationsByDay = registrationsByDay_.reduce(
      (acc: IRegistrationByDayInterface[], currentItem) => {
        // Check if the riderId of the current item is already in the accumulator
        const hasRider = acc.some(
          (item) =>
            item.uuid === currentItem.uuid &&
            item.riderId === currentItem.riderId &&
            isEqual(item.horsesSelected, currentItem.horsesSelected)
        )

        // If riderId doesn't exist, add the current item to the accumulator
        if (!hasRider) acc.push(currentItem)
        return acc
      },
      []
    )

    setRegistrationsByDay(uniqueRegistrationsByDay)
    return uniqueRegistrationsByDay
  }

  useEffect(() => {
    const getEventStaff = async () => {
      const publishedEventInDb = await EventReviewPublishService.getById(eventId)
      setPublishedEvent(publishedEventInDb)
      const judgesStaff_ = publishedEventInDb?.EventStaff?.eventStaff?.find(
        (staff) => staff.title === 'Judges'
      )

      if (judgesStaff_?.value && judgesStaff_.value.length > 0) {
        const usersSnaps = await FirestoreService.getItemsUsingIds(
          COLLECTIONS.USERS.NAME,
          judgesStaff_.value
        )

        const usersList: IUserInterface[] = []
        usersSnaps.forEach((currSnap) =>
          usersList.push(getConvertedData(UserModel.fromFirestoreDoc(currSnap).toObject()))
        )

        if (usersList.length > 0)
          setJudgesOptions(
            usersList.map((user) => ({
              label: getUserFullName(user),
              value: user.id,
              disable: false,
            }))
          )
      }
    }
    if (eventId) getEventStaff().then()
  }, [eventId, registrationFees])

  useEffect(() => {
    const handleGetFeesData = async () => {
      const eventFeesSnapShot = await FirestoreService.getItem(COLLECTIONS.EVENT_FEES.NAME, eventId)

      const registrationFees_ = new EventFeesModel(eventFeesSnapShot.data()).toFirestore()

      const registrationsByDay_ = await getRegistrationsByDay()

      // fetch the event scheduling
      const eventSchedulingSnapShot = await FirestoreService.getItem(
        COLLECTIONS.EVENT_SCHEDULING.NAME,
        eventId
      )
      // default to class for backwards compatability
      const scheduleByDataPoint = eventSchedulingSnapShot?.data()?.scheduleBy || 'class'

      // registrationFees key in the eventFeesSnapShot data is an array of classes for the event
      let fees_: IManageOption[] = registrationFees_.registrationFees.flatMap(
        (registrationFee: RegistrationFeesType) => {
          const count = registrationsByDay_?.reduce(
            (acc, itm) => (itm.uuid === registrationFee.uuid ? acc + 1 : acc),
            0
          )

          const feeLabel = `${registrationFee.order ? `#${registrationFee.order} •` : ''} ${registrationFee.name || ''} <span class="text-SeabiscuitDark200ThemeColor/50"> • ${count || 0} Rides</span>`

          const childrenFees = registrationFee.children?.map((child) => {
            const childCount = registrationsByDay_?.reduce(
              (acc, itm) => (itm.uuid === child.uuid ? acc + 1 : acc),
              0
            )

            return {
              value: child.uuid || '',
              label: `${child.order ? `#${child.order} •` : ''} ${child.name || ''} <span class="text-SeabiscuitDark200ThemeColor/50"> • ${childCount} Rides</span>`,
              parentId: registrationFee.uuid,
              entryCount: childCount,
              facility: child.location,
              className: child.name || '',
              order: child.order || 0,
            }
          })

          if (childrenFees && childrenFees.length > 0) {
            return [...childrenFees]
          } else {
            return [
              {
                value: registrationFee.uuid || '',
                label: feeLabel,
                entryCount: count || 0,
                facility: registrationFee.location,
                className: registrationFee.name || '',
                order: Number(registrationFee.order) || 0,
              },
            ]
          }
        }
      )

      fees_ = fees_.sort((a, b) => (a?.order || 0) - (b?.order || 0)) || []

      setScheduleBy(scheduleByDataPoint)
      setClassOptions(fees_)
      setRegistrationFees(registrationFees_)
    }
    if (eventId) handleGetFeesData().then()
  }, [eventId])

  useEffect(() => {
    const options = Object.keys(registrationsGroupedByDay)
      .map((groupKey) => {
        const [dayKey, facility] = groupKey.split('_')

        // create date object from dayKey,
        const normalizeDate = parse(dayKey, 'yyyy-MM-dd', new Date())

        // format day name for display label
        const formattedDayLabel = format(normalizeDate, 'EEEE')

        const validEntries = registrationsGroupedByDay[groupKey].filter(
          (entry) => !entry.isBreakDrag
        )

        return {
          label: `${formattedDayLabel} Rides • ${facility} <span style="color: #8795A2;">${`• ${validEntries.length} entries`}`,
          value: groupKey,
          entryCount: validEntries.length,
          facility: facility,
          formattedDay: formattedDayLabel,
        }
      })
      .sort((a, b) => b.entryCount - a.entryCount)

    setDayOptions(options)
  }, [registrationsGroupedByDay])

  useEffect(() => {
    const getRidersByClass = async () => {
      try {
        let registrationsByDayByClass_: IRegistrationByDayInterface[] =
          registrationsByDay?.filter((registrationByDay) => registrationByDay.uuid === fee?.uuid) ||
          []

        const usersInDb = await UserService.getByIds(fee?.judges || [])
        const { judges, sheet } = await getJudgesAndSheet({
          discipline: fee?.discipline || 'Dressage',
          sheetId: fee?.scoreCard,
          judgeIds: fee?.judges || [],
          users: usersInDb,
        })
        let count = 0
        registrationsByDayByClass_ = registrationsByDayByClass_.reduce(
          (acc: IRegistrationByDayInterface[], rider) => {
            const newItem = {
              ...rider,
              score: {
                ...rider.score,
                judges: rider.score?.judges?.[0] ? rider.score?.judges : judges || [],
                sheet: rider.score?.sheet?.name
                  ? rider.score?.sheet
                  : fee?.scoreCard
                    ? sheet
                    : null,
              },
              order:
                rider.orderOfGoScratched || rider.order === 0
                  ? // || rider.isScratched
                    0
                  : rider.order || count + 1,
            }
            if (!rider.orderOfGoScratched && rider.order !== 0) count++

            acc.push(newItem)
            return acc
          },
          []
        )

        if (activeTab === MANAGE_OPERATIONS_CONSTS.TAB_NAMES.ORDER.value) {
          // Sort array depends on order
          const event = currentEvent?.Event

          // New IBreak is optional for class to support the rider group day based scheduling, now we explicitly check that the break object has the class object and a value when filtering
          const breaksInDb: IBreak[] =
            event && Array.isArray(event.breaks)
              ? event.breaks.filter(
                  (itm) => itm.registrationByDayUuid === fee?.uuid && !itm.groupKey
                )
              : []
          setBreaks(breaksInDb)
        } else if (activeTab === MANAGE_OPERATIONS_CONSTS.TAB_NAMES.PRIZE_MONEY.value) {
          const scoreCardType = registrationsByDayByClass_?.[0]?.score?.judges?.[0]?.scoreCardType
          if (scoreCardType === 'fastest') {
            registrationsByDayByClass_ = sortResultsByTime(
              registrationsByDayByClass_,
              fee?.isAsc ? 'asc' : 'desc',
              'fastest'
            )
          } else if (scoreCardType === 'optimum') {
            registrationsByDayByClass_ = sortResultsByTime(
              registrationsByDayByClass_,
              fee?.isAsc ? 'asc' : 'desc',
              'optimal'
            )
          } else {
            registrationsByDayByClass_ = sortByTotalPoints(
              registrationsByDayByClass_,
              fee?.isAsc ? 'asc' : 'desc'
            )
          }
        }
        setRegistrationsByDayByClass(registrationsByDayByClass_)
      } catch (error) {
        console.error(error, 'error')
      }
    }
    if (activeTab && (scheduleBy === 'class' || activeTab === 'prize-money') && fee) {
      getRidersByClass().then()
    }
  }, [activeTab, currentEvent?.Event, fee, registrationsByDay, scheduleBy])

  useEffect(() => {
    const getRidersByDay = async (activeTab: string) => {
      try {
        const eventSnapShot = await FirestoreService.getItem(COLLECTIONS.EVENTS.NAME, eventId)
        const eventData = eventSnapShot.data()

        const eventBreaks: any[] = eventData?.breaks || []
        const flattenedFees = getAllRegistrationFees(registrationFees?.registrationFees || [])
        const groupFeesByDayAndFacility = getGroupFeesByDayAndFacility(flattenedFees)

        const groupedByDayFacility: Record<string, any[]> = {}

        for (const [groupKey, { fees }] of Object.entries(groupFeesByDayAndFacility)) {
          const feeUuids = fees.map(({ uuid }) => uuid)

          // get all registrations that belong to any fee UUID for this day/facility group
          const registrationsForGroupFiltered =
            registrationsByDay?.filter(
              (registration: IRegistrationByDayInterface) =>
                registration.uuid &&
                feeUuids.includes(registration.uuid) &&
                (registration.paymentType !== PAYMENT_TYPE.DEFAULT ||
                  registration.paymentStatus !== PAYMENT_STATUS.DEFAULT)
            ) || []

          let registrationsForGroup: (IRegistrationByDayInterface & { fee?: FeesTypeItem })[] = []

          let count = 0
          // Enrich each registration with its matching fee's scoring details
          for (const rider of registrationsForGroupFiltered) {
            // Find the fee whose UUID matches the registration's UUID
            const matchingFee = flattenedFees?.find(
              (feeItem: RegistrationFeesType) => feeItem.uuid === rider.uuid
            )

            let judges: IJudge[] = []
            let sheet = null

            if (!rider.score) {
              const usersInDb = await UserService.getByIds(matchingFee?.judges || [])

              // Get the scoring sheet from the sheet array if it is a new registration by day
              const judgesAndSheet = await getJudgesAndSheet({
                discipline: matchingFee?.discipline || 'Dressage',
                sheetId: matchingFee?.scoreCard || '',
                judgeIds: matchingFee?.judges || [],
                users: usersInDb,
              })
              judges = judgesAndSheet?.judges || []
              sheet = judgesAndSheet?.sheet || null
            }

            // Build the enriched score object
            const enrichedScore = {
              ...rider.score,
              judges: rider.score?.judges?.[0] ? rider.score.judges : judges || [],
              sheet: rider.score?.sheet?.name
                ? rider.score.sheet
                : matchingFee?.scoreCard
                  ? sheet
                  : null,
            }

            registrationsForGroup.push({
              ...rider,
              order:
                rider.orderOfGoScratched || rider.order === 0
                  ? // || rider.isScratched
                    0
                  : rider.order || count + 1,
              score: enrichedScore,
              // add the matching fee to state so we can reference the correct fee for rider based logic
              fee: matchingFee,
            })
            if (!rider.orderOfGoScratched && rider.order !== 0) count++
          }

          let finalRegistrations = registrationsForGroup

          if (activeTab === MANAGE_OPERATIONS_CONSTS.TAB_NAMES.ORDER.value) {
            // getting the breaks on the event object for the day and merging only if in order of go tab
            const breaksForGroup = eventBreaks.filter((brk) => brk.groupKey === groupKey)
            finalRegistrations = [...registrationsForGroup, ...breaksForGroup]
          }

          groupedByDayFacility[groupKey] = sortByOrder(finalRegistrations)
        }

        setRegistrationsGroupedByDay(groupedByDayFacility)
      } catch (error) {
        console.error(error, 'error')
      }
    }
    if (activeTab && eventId && scheduleBy === 'rider' && activeTab !== 'prize-money') {
      getRidersByDay(activeTab).then()
    }
  }, [activeTab, registrationsByDay, scheduleBy, eventId, registrationFees?.registrationFees])

  useEffect(() => {
    if (selectedClass?.value && registrationFees?.registrationFees) {
      let selectedFee: RegistrationFeesType | null = null

      if (registrationFees.registrationFees && Array.isArray(registrationFees.registrationFees)) {
        registrationFees.registrationFees.forEach((innerFee) => {
          if (innerFee.uuid === selectedClass?.value) {
            selectedFee = innerFee
          }

          if (innerFee.children && Array.isArray(innerFee.children)) {
            innerFee.children.forEach((innerChildFee) => {
              if (innerChildFee.uuid === selectedClass?.value) {
                selectedFee = innerChildFee
              }
            })
          }
        })
      }

      setFee(selectedFee)
    }
  }, [registrationFees?.registrationFees, selectedClass?.value])

  const updateFeesKeys: IUpdateFeesKeys = async (keys, selectedUuIds) => {
    const updatedRegistrationFees = cloneDeep(registrationFees?.registrationFees || [])

    for (const selectedUuId of selectedUuIds) {
      const parentIndex = registrationFees?.registrationFees?.findIndex(
        (item) => item.uuid === selectedUuId.parentId
      )

      if (selectedUuId.parentId && parentIndex !== -1) {
        const indexToUpdateFees = registrationFees?.registrationFees?.[
          parentIndex!
        ].children?.findIndex((item) => item.uuid === selectedUuId.uuid)

        const targetItem = updatedRegistrationFees[parentIndex!]?.children?.[indexToUpdateFees!]!
        updatedRegistrationFees[parentIndex!].children![indexToUpdateFees!] = {
          ...targetItem,
          ...keys,
        }
      } else {
        const indexToUpdateFees = registrationFees?.registrationFees?.findIndex(
          (item) => item.uuid === selectedUuId.uuid
        )
        const targetItem = updatedRegistrationFees[indexToUpdateFees!]
        updatedRegistrationFees[indexToUpdateFees!] = { ...targetItem, ...keys }
      }
    }

    setRegistrationFees({ ...registrationFees, registrationFees: updatedRegistrationFees })

    await FirestoreService.updateItem(COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME, eventId, {
      EventFees: {
        ...registrationFees,
        registrationFees: updatedRegistrationFees,
      },
    })

    await FirestoreService.updateItem(COLLECTIONS.EVENT_FEES.NAME, eventId, {
      registrationFees: updatedRegistrationFees,
    })
  }

  const updateRegistrationByDayName = async ({
    feeUuid,
    sheetId,
    discipline,
    judgesValues,
  }: IUpdateRegistrationByDayName) => {
    const usersInDb = await UserService.getByIds(judgesValues || [])

    const { judges, sheet } = await getJudgesAndSheet({
      discipline,
      sheetId,
      judgeIds: judgesValues || [],
      users: usersInDb,
    })

    const registrationsByDayToUpdate: IRegistrationByDayInterface[] = []
    const registrationsByDayUpdated =
      registrationsByDay?.map((item) => {
        if (item.uuid && feeUuid.includes(item.uuid)) {
          const current = {
            ...item,
            score: {
              totalPoints: null,
              details: null,
              rank: null,
              file: null,
              earning: null,
              sheet,
              judges,
            },
          }
          registrationsByDayToUpdate.push(current)
          return current
        } else {
          return item
        }
      }) || []

    if (registrationsByDayToUpdate?.[0]) {
      setRegistrationsByDay(registrationsByDayUpdated)
      await httpService({
        url: 'update_registrations_by_day',
        method: 'POST',
        data: {
          registrationsByDay: registrationsByDayToUpdate,
        },
      })
    }
  }

  const onChangeJudge = async (
    row: RegistrationFeesTypeEx,
    values: { label: string; value: string }[]
  ) => {
    const judges = values?.map((value) => value.value)
    await updateFeesKeys({ judges: judges }, [{ uuid: row.uuid, parentId: row.parentId }])
    await updateRegistrationByDayName({
      feeUuid: [row.uuid],
      sheetId: row.scoreCard || '',
      discipline: row.discipline || null,
      judgesValues: [],
    })
  }

  const onSortClassScore = async (isAsc: boolean) => {
    try {
      const registrationsByDayToUpdate: IRegistrationByDayInterface[] = []
      let sortedRegistrations: IRegistrationByDayInterface[]
      let count = 0

      const scoreCardType = registrationsByDay?.find((item) => item.uuid === selectedClass?.value)
        ?.score?.judges?.[0]?.scoreCardType
      if (scoreCardType === 'fastest') {
        sortedRegistrations = sortResultsByTime(
          registrationsByDay,
          isAsc ? 'asc' : 'desc',
          'fastest'
        )
      } else if (scoreCardType === 'optimum') {
        sortedRegistrations = sortResultsByTime(
          registrationsByDay,
          isAsc ? 'asc' : 'desc',
          'optimal'
        )
      } else {
        sortedRegistrations = sortByTotalPoints(registrationsByDay, isAsc ? 'asc' : 'desc')
      }

      // update rank for all registrations that have the same class
      sortedRegistrations?.forEach((registrationByDay) => {
        if (selectedClass?.value === registrationByDay.uuid) {
          count++
          const current = {
            ...registrationByDay,
            ...(registrationByDay.score && {
              score: { ...registrationByDay.score, rank: count },
            }),
          }
          registrationsByDayToUpdate.push(current)
          return current
        }
      })

      await updateFeesKeys({ isAsc }, [
        { uuid: selectedClass?.value || '', parentId: selectedClass?.parentId },
      ])

      // Update firestore collection on BE by batch
      if (registrationsByDayToUpdate?.[0]) {
        await httpService({
          url: 'update_registrations_by_day',
          method: 'POST',
          data: {
            registrationsByDay: registrationsByDayToUpdate,
          },
        })
      }

      const registrationsByDayNew = registrationsByDay.map((item) => {
        const current = registrationsByDayToUpdate.find((current) => current.id === item.id)
        if (current) return current
        else return item
      })

      setRegistrationsByDay([...registrationsByDayNew])
    } catch (e) {
      console.error('Error while sorting class score: ', e)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  const onScore = async (row: IRegistrationByDayEx) => {
    try {
      const flattenedFees = getAllRegistrationFees(registrationFees?.registrationFees || [])
      // Find the fee whose UUID matches the registration's UUID
      const matchingFee = flattenedFees?.find(
        (feeItem: RegistrationFeesType) => feeItem.uuid === row.uuid
      )

      const usersInDb = await UserService.getByIds(matchingFee?.judges || [])

      const { judges, sheet } = await getJudgesAndSheet({
        discipline: matchingFee?.discipline || 'Dressage',
        sheetId: matchingFee?.scoreCard || '',
        judgeIds: matchingFee?.judges || [],
        users: usersInDb,
      })

      const rowUpdated: IRegistrationByDayInterface = {
        ...row,
        score: {
          ...row.score,
          judges: row.score?.judges?.[0] ? row.score.judges : judges || [],
          sheet: row.score?.sheet?.name ? row.score.sheet : matchingFee?.scoreCard ? sheet : null,
        },
      }

      if (row.score?.sheet?.type === 'hunter-jumper') {
        handleModalContext?.handleModal(true, MODAL_CONSTS.CUSTOM_SCORING, {
          modalTitle: 'Results',
          rider: rowUpdated,
          onSaveScoring: getRegistrationsByDay,
          onChangeJudge: onChangeJudge,
          editable: true,
        })
      } else {
        handleModalContext?.handleModal(true, MODAL_CONSTS.SCORING, {
          rider: rowUpdated,
          onSaveScoring: getRegistrationsByDay,
          onChangeJudge: onChangeJudge,
          editable: true,
        })
      }
    } catch (error) {
      console.error('error: ', error)
    }
  }

  const fileChangedHandler = async (row: IRegistrationByDayInterface, files: FileList | null) => {
    const file = files?.[0]

    if (!file) {
      toastFunctions.success({ message: MESSAGES_CONST.NO_FILE_SELECTED })
      return
    }

    try {
      const downloadUrl = await FirebaseStorageService.uploadFile(file, `${PATH}/${userId}`)
      const scoreToUpdate = { ...row.score, file: downloadUrl as string }

      const registrationsByDayUpdated = registrationsByDay.map((item) => {
        if (item.id === row.id) {
          return { ...item, score: scoreToUpdate }
        } else {
          return item
        }
      })

      setRegistrationsByDay([...registrationsByDayUpdated])

      await FirestoreService.updateItem(COLLECTIONS.REGISTRATION_BY_DAY.NAME, row.id, {
        score: scoreToUpdate,
      })

      toastFunctions.success({ message: MESSAGES_CONST.EVENT_SCORE_UPLOAD })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      console.error('error: ', error)
    }
  }

  if (!scheduleBy) {
    return (
      <div className="w-full min-h-[400px] flex justify-center items-center">
        <ViewsLoader size="lg" color="#F70763" />
      </div>
    )
  }

  return (
    <div className="p-5 rounded-xl bg-SeabiscuitWhiteThemeColor w-full flex flex-col">
      <OperationsHeader
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        selectedClass={selectedClass}
        setSelectedClass={handleSetSelectedClass}
        classOptions={classOptions}
        selectedDay={selectedDay}
        setSelectedDay={handleSetSelectedDay}
        dayOptions={dayOptions}
        registrationFees={registrationFees}
        setIncrement={setIncrement}
        updateFeesKeys={updateFeesKeys}
        registrationsGroupedByDay={registrationsGroupedByDay}
        setRegistrationsByDay={setRegistrationsByDay}
        registrationsByDay={registrationsByDay}
        fee={fee}
        eventId={eventId}
        scheduleBy={scheduleBy}
        setRegistrationsGroupedByDay={setRegistrationsGroupedByDay}
        setGapTime={handleSetGapTime}
        clashCount={clashCount}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        isTableInEditMode={isEditMode}
        publishedEvent={publishedEvent}
        setPublishedEvent={setPublishedEvent}
      />

      {activeTab === MANAGE_OPERATIONS_CONSTS.TAB_NAMES.JUDGING.value ? (
        <JudgingTable
          updateFeesKeys={updateFeesKeys}
          registrationFees={registrationFees}
          judgesOptions={judgesOptions}
          onChangeJudge={onChangeJudge}
          updateRegistrationByDayName={updateRegistrationByDayName}
        />
      ) : (
        <div className="relative z-10 mt-8">
          {activeTab === MANAGE_OPERATIONS_CONSTS.TAB_NAMES.ORDER.value && (
            <>
              {scheduleBy === 'class' && (
                <OrderOfGoTableByClass
                  selectedClass={selectedClass}
                  breaks={breaks}
                  setBreaks={setBreaks}
                  registrationsByDayByClass={registrationsByDayByClass}
                  setRegistrationsByDayByClass={setRegistrationsByDayByClass}
                  increment={increment}
                  fee={fee}
                  eventId={eventId}
                  registrationsByDay={registrationsByDay}
                  setRegistrationsByDay={setRegistrationsByDay}
                />
              )}
              {scheduleBy === 'rider' && selectedDay && (
                <OrderOfGoTableByRider
                  eventId={eventId}
                  selectedDay={selectedDay}
                  registrationsGroupedByDay={registrationsGroupedByDay}
                  registrationsByDay={registrationsByDay}
                  setRegistrationsByDay={handleSetRegistrationsByDay}
                  gapTime={gapTime}
                  onClashCountChange={setClashCount}
                  isEditMode={isEditMode}
                  onToggleEditMode={() => setIsEditMode((prev) => !prev)}
                  searchValue={searchValue}
                />
              )}
            </>
          )}

          {activeTab === MANAGE_OPERATIONS_CONSTS.TAB_NAMES.SCORING.value && (
            <>
              {scheduleBy === 'class' && (
                <ScoringTableByClass
                  registrationsByDayByClass={registrationsByDayByClass}
                  breaks={breaks}
                  registrationsByDay={registrationsByDay}
                  setRegistrationsByDay={setRegistrationsByDay}
                  registrationFees={registrationFees}
                  increment={increment}
                  onScore={onScore}
                  fileChangedHandler={fileChangedHandler}
                />
              )}
              {scheduleBy === 'rider' && selectedDay && (
                <ScoringTableByRider
                  selectedDay={selectedDay}
                  registrationsGroupedByDay={registrationsGroupedByDay}
                  registrationsByDay={registrationsByDay}
                  setRegistrationsByDay={handleSetRegistrationsByDay}
                  searchValue={searchValue}
                  registrationFees={registrationFees}
                  onScore={onScore}
                  fileChangedHandler={fileChangedHandler}
                />
              )}
            </>
          )}

          {activeTab === MANAGE_OPERATIONS_CONSTS.TAB_NAMES.PRIZE_MONEY.value &&
            registrationsByDayByClass.length > 0 && (
              <PrizeMoneyTable
                registrationsByDayByClass={registrationsByDayByClass}
                registrationsByDay={registrationsByDay}
                setRegistrationsByDay={setRegistrationsByDay}
                eventId={eventId}
                registrationFees={registrationFees}
                selectedClass={selectedClass}
                onSortClassScore={onSortClassScore}
                fee={fee}
                currency={currency}
              />
            )}
        </div>
      )}
    </div>
  )
}

export default ManageClinicNOtherOperationsTab
